import { forwardRef } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';

export interface AppTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Helper text shown below the input (e.g., character count)
   */
  helperText?: React.ReactNode;
  /**
   * Error message to display
   */
  errorText?: string;
  /**
   * Show character count (requires maxLength on inputProps)
   */
  showCharCount?: boolean;
  /**
   * Current character count (if showCharCount is true)
   */
  charCount?: number;
  /**
   * Maximum characters allowed
   */
  maxLength?: number;
}

/**
 * AppTextField - Standard text input component
 *
 * Wraps MUI TextField with our design system patterns.
 * Handles error states, helper text, and character counting.
 */
export const AppTextField = forwardRef<HTMLInputElement, AppTextFieldProps>(
  (
    {
      errorText,
      helperText,
      showCharCount,
      charCount = 0,
      maxLength,
      error,
      slotProps,
      ...props
    },
    ref
  ) => {
    const hasError = error || !!errorText;
    const isNearLimit = maxLength && charCount > maxLength * 0.9;

    return (
      <Box sx={{ width: '100%' }}>
        <TextField
          ref={ref}
          variant="outlined"
          size="small"
          fullWidth
          error={hasError}
          slotProps={{
            ...slotProps,
            input: {
              ...slotProps?.input,
              inputProps: {
                maxLength,
                ...(slotProps?.input as { inputProps?: Record<string, unknown> })?.inputProps,
              },
            },
          }}
          {...props}
        />
        {(errorText || helperText || showCharCount) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mt: 0.5,
              px: 0.5,
            }}
          >
            {errorText ? (
              <FormHelperText error sx={{ m: 0, fontSize: '0.75rem', fontWeight: 600 }}>
                {errorText}
              </FormHelperText>
            ) : helperText ? (
              <FormHelperText sx={{ m: 0, fontSize: '0.75rem' }}>{helperText}</FormHelperText>
            ) : (
              <span />
            )}
            {showCharCount && maxLength && (
              <FormHelperText
                sx={{
                  m: 0,
                  fontSize: '0.75rem',
                  color: isNearLimit ? 'warning.main' : 'text.secondary',
                  fontWeight: isNearLimit ? 500 : 400,
                }}
              >
                {charCount}/{maxLength}
              </FormHelperText>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

AppTextField.displayName = 'AppTextField';
